import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Mic, Users, Brain, BookOpen, Lightbulb, Clock, MapPin, MessageSquare, CheckCircle2 } from 'lucide-react';
import { Section, SectionTitle, AnimatedCard } from '@/components/Section';
import { Button } from '@/components/ui/button';

const topics = [
  {
    title: 'Practical AI for Business',
    description: 'How artificial intelligence is reshaping operations, decision-making, and productivity. Grounded perspective beyond the hype.',
    icon: Brain,
    subtopics: ['AI Strategy for Business', 'Human-AI Collaboration', 'Operational Automation', 'Future of Work']
  },
  {
    title: 'Entrepreneurship & Systems Thinking',
    description: 'Building ventures that matter. From ideation to execution, the practical wisdom of entrepreneurial success and systems design.',
    icon: Lightbulb,
    subtopics: ['Starting with Limited Resources', 'Scaling Sustainably', 'Entrepreneurial Mindset', 'Systems Architecture']
  },
  {
    title: 'The Future of Publishing',
    description: 'How technology is transforming publishing and content creation. Opportunities for authors, publishers, and organizations.',
    icon: BookOpen,
    subtopics: ['AI in Publishing', 'Independent Author Success', 'Content Monetization', 'Digital Distribution']
  },
  {
    title: 'Legacy & Human Impact',
    description: 'Preserving stories, wisdom, and identity in an age of digital abundance. Building meaningful legacies through technology.',
    icon: Users,
    subtopics: ['Digital Legacy Preservation', 'Storytelling Strategy', 'Generational Knowledge Transfer', 'Technology as Human Amplifier']
  }
];

const engagements = [
  {
    title: 'Keynote Address',
    duration: '45\u201360 minutes',
    description: 'High-impact presentation for conferences, business groups, and professional organizations. Tailored to audience and event themes with practical perspective on technology, entrepreneurship, and the future of work.',
    icon: Mic
  },
  {
    title: 'Strategic Roundtable',
    duration: 'Advisory / Informal Session',
    description: 'A conversational session for leadership teams, boards, mastermind groups, or organizations exploring the practical impact of AI, automation, and emerging technologies on operations, workflow, and strategic direction.',
    icon: MessageSquare
  },
  {
    title: 'Panel Discussion',
    duration: 'As Needed',
    description: 'Thoughtful participation in moderated discussions on AI, publishing, entrepreneurship, innovation, and the human implications of technological change.',
    icon: Users
  }
];

const speakerBio = [
  'Robert Brunet brings more than four decades of experience across technology, entrepreneurship, publishing, and emerging AI systems to every presentation and advisory conversation.',
  'His perspective is grounded not in theory alone, but in real-world building\u2014launching ventures, designing systems, publishing ideas, and helping others make sense of rapidly changing technology.',
  'Whether speaking to entrepreneur groups, business organizations, leadership teams, or conference audiences, Robert brings a practical, systems-oriented voice focused on what matters most: how emerging technologies can be understood, evaluated, and applied in the real world.',
  'His style is direct, thoughtful, and substance-driven\u2014designed to leave audiences with useful perspective rather than empty hype.'
];

const considerations = [
  'Event type',
  'Audience size',
  'Travel requirements',
  'Format and duration',
  'Advisory vs. keynote participation'
];

export default function SpeakingBackupPage() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block">Speaking & Advisory</span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-testid="speaking-headline">
              Thought Leadership
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Keynotes, panel discussions, and strategic conversations on AI, entrepreneurship, publishing, and human potential. Practical insights from decades of building, systems thinking, and real-world innovation.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="book-speaking-btn">
                <Link to="/contact">Request Engagement</Link>
              </Button>
              <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="download-kit-btn">
                <a href="#speaker-bio">View Speaker Profile</a>
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/sw7cidcp_R_Dale_Brunet_AI_Bodyshot_1.png"
              alt="R. Dale Brunet"
              className="rounded-xl w-full max-w-lg mx-auto"
            />
          </motion.div>
        </div>
      </Section>

      {/* Why This Perspective Matters */}
      <Section dark>
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm text-primary font-medium uppercase tracking-widest mb-3 block">Credibility</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white" data-testid="why-perspective-heading">
              Why This Perspective Matters
            </h2>
          </motion.div>

          <div className="space-y-4">
            <motion.p
              className="text-lg text-slate-300 leading-relaxed"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              R. Dale Brunet brings over 30 years of hands-on experience designing and building real-world systems across industries including banking, defense, aerospace, and nonprofit organizations.
            </motion.p>
            <motion.p
              className="text-lg text-slate-300 leading-relaxed"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              His work has centered on creating tools that track, analyze, and support decision-making at scale—long before artificial intelligence entered the mainstream conversation.
            </motion.p>
            <motion.p
              className="text-lg text-white font-medium leading-relaxed"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Today, that experience translates into a practical, no-nonsense approach to AI—focused not on theory, but on real operational impact.
            </motion.p>
          </div>
        </div>
      </Section>

      {/* Speaking Themes */}
      <Section>
        <SectionTitle subtitle="Topics">Speaking Themes</SectionTitle>

        <div className="grid md:grid-cols-2 gap-6">
          {topics.map((topic, index) => (
            <AnimatedCard key={topic.title} delay={index * 0.1}>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <topic.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                </div>
              </div>

              <div className="ml-16 flex flex-wrap gap-2">
                {topic.subtopics.map((subtopic) => (
                  <span
                    key={subtopic}
                    className="text-xs px-3 py-1 rounded-full bg-secondary text-slate-300"
                  >
                    {subtopic}
                  </span>
                ))}
              </div>
            </AnimatedCard>
          ))}
        </div>
      </Section>

      {/* Engagement Options */}
      <Section dark>
        <SectionTitle subtitle="Formats">Engagement Options</SectionTitle>

        <div className="grid md:grid-cols-3 gap-6">
          {engagements.map((eng, index) => (
            <motion.div
              key={eng.title}
              className="bg-card border border-border rounded-xl p-8 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`engagement-${index}`}
            >
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <eng.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-1">{eng.title}</h3>
              <span className="text-sm text-primary mb-4 block">{eng.duration}</span>
              <p className="text-sm text-muted-foreground leading-relaxed">{eng.description}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Engagement Considerations */}
      <Section>
        <SectionTitle subtitle="Details">Engagement Considerations</SectionTitle>

        <div className="grid lg:grid-cols-3 gap-8">
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="space-y-4 text-slate-300 leading-relaxed">
              <p>
                Speaking and advisory engagements vary depending on the nature of the event, audience, location, and level of participation requested.
              </p>
              <p>
                For events outside the Colorado Springs region, hosts typically cover standard travel-related expenses such as transportation, lodging, and meals when applicable.
              </p>
              <p>
                A limited number of local entrepreneur, nonprofit, or community-focused events may be considered with flexible arrangements when schedules permit.
              </p>
              <p>
                Organizations interested in presentations, panels, or strategic advisory conversations are encouraged to reach out to discuss format, availability, and fit.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="text-sm font-semibold text-primary uppercase tracking-widest mb-4">Typical Considerations</h4>
              <ul className="space-y-3">
                {considerations.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Speaker Profile */}
      <Section dark id="speaker-bio">
        <SectionTitle subtitle="Biography">Speaker Profile</SectionTitle>

        <div className="grid lg:grid-cols-3 gap-8">
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {speakerBio.map((paragraph) => (
              <p key={paragraph.slice(0, 50)} className="text-lg text-slate-300 leading-relaxed mb-5">
                {paragraph}
              </p>
            ))}
          </motion.div>

          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_d94f9753-7cd9-433d-b7b2-74888a26953a/artifacts/zjadlivq_R_Dale_Brunet_AI_4.jpg"
              alt="R. Dale Brunet speaking on stage"
              className="rounded-xl w-full object-cover"
            />
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="text-sm font-semibold text-primary uppercase tracking-widest mb-4">Quick Facts</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-300">
                  <Clock className="w-4 h-4 text-primary" />
                  40+ years technology experience
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <Lightbulb className="w-4 h-4 text-primary" />
                  6 active ventures
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <BookOpen className="w-4 h-4 text-primary" />
                  40+ published titles
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <MapPin className="w-4 h-4 text-primary" />
                  Based in Colorado Springs
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="speaking-cta-headline">
            Request a Speaking or Advisory Conversation
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Interested in a keynote, panel discussion, strategic roundtable, or informal advisory conversation? Let's discuss the right format for your audience, organization, or event.
          </p>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="inquire-speaking-btn">
            <Link to="/contact">Request Engagement <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </Section>
    </main>
  );
}
